import {
  defaultRecommendationAssumptionSet,
  type RecommendationModelCoefficients,
  type RecommendationOptionsInput,
} from "../../../configurator";
import type { RecommendationOptionsFormValues } from "../../../web-app";

export const MODEL_COEFFICIENT_FIELDS: Array<{
  key: keyof RecommendationModelCoefficients;
  label: string;
}> = [
  { key: "powerUtilizationBase", label: "Power utilization base" },
  { key: "powerUtilizationFrequencyCoefficient", label: "Power frequency coefficient" },
  { key: "powerUtilizationVoltageCoefficient", label: "Power voltage coefficient" },
  { key: "powerUtilizationCurrentCoefficient", label: "Power current coefficient" },
  {
    key: "powerUtilizationPipeOutsideDiameterCoefficient",
    label: "Pipe OD coefficient",
  },
  {
    key: "powerUtilizationPipeWallThicknessCoefficient",
    label: "Wall thickness coefficient",
  },
  {
    key: "powerUtilizationFrameDiameterCoefficient",
    label: "Frame diameter coefficient",
  },
  { key: "powerUtilizationTurnsCoefficient", label: "Turns coefficient" },
  {
    key: "powerUtilizationBottomEndWidthCoefficient",
    label: "Bottom end width coefficient",
  },
  { key: "powerUtilizationTurnSpacingCoefficient", label: "Turn spacing coefficient" },
  { key: "heatingCycleFrequencyCoefficient", label: "Heating frequency coefficient" },
  { key: "cooldownFrequencyCoefficient", label: "Cooldown frequency coefficient" },
  {
    key: "cooldownHeatingCycleHeatSoakCoefficient",
    label: "Cooldown heat soak coefficient",
  },
  {
    key: "cooldownPipeWallThicknessCoefficient",
    label: "Cooldown wall thickness coefficient",
  },
];

function resolveDisplayAssumptions(options: RecommendationOptionsInput) {
  const modelCoefficients = {
    ...defaultRecommendationAssumptionSet.advancedCalibration.modelCoefficients,
    ...options.advancedCalibration?.modelCoefficients,
    ...options.modelCoefficients,
  };

  return {
    referenceFrequencyHz:
      options.referenceFrequencyHz ?? defaultRecommendationAssumptionSet.referenceFrequencyHz,
    preferredTurnSpacingMm:
      options.preferredTurnSpacingMm ??
      defaultRecommendationAssumptionSet.preferredTurnSpacingMm,
    turnBounds: {
      minimumTurns:
        options.turnBounds?.minimumTurns ??
        defaultRecommendationAssumptionSet.turnBounds.minimumTurns,
      maximumTurns:
        options.turnBounds?.maximumTurns ??
        defaultRecommendationAssumptionSet.turnBounds.maximumTurns,
    },
    coilClearanceMm:
      options.coilClearanceMm ?? defaultRecommendationAssumptionSet.coilClearanceMm,
    requiredOverhangMm:
      options.requiredOverhangMm ?? defaultRecommendationAssumptionSet.requiredOverhangMm,
    tieBarDiameterMm:
      options.tieBarDiameterMm ?? defaultRecommendationAssumptionSet.tieBarDiameterMm,
    frequencyCalibrationCurve:
      options.frequencyCalibrationCurve?.map((point) => ({ ...point })) ??
      defaultRecommendationAssumptionSet.frequencyCalibrationCurve.map((point) => ({ ...point })),
    advisoryThresholds: {
      ...defaultRecommendationAssumptionSet.advisoryThresholds,
      ...options.advisoryThresholds,
    },
    modelCoefficients,
  };
}

export function assumptionsToFormValues(
  assumptions: RecommendationOptionsInput,
): RecommendationOptionsFormValues {
  const resolved = resolveDisplayAssumptions(assumptions);
  const values = {
    referenceFrequencyHz: String(resolved.referenceFrequencyHz),
    preferredTurnSpacingMm: String(resolved.preferredTurnSpacingMm),
    minimumTurns: String(resolved.turnBounds.minimumTurns),
    maximumTurns: String(resolved.turnBounds.maximumTurns),
    coilClearanceMm: String(resolved.coilClearanceMm),
    requiredOverhangMm: String(resolved.requiredOverhangMm),
    tieBarDiameterMm: String(resolved.tieBarDiameterMm),
    frequencyCalibrationPoints: resolved.frequencyCalibrationCurve.map((point) => ({
      frequencyHz: String(point.frequencyHz),
      heatingCycleFactor: String(point.heatingCycleFactor),
      cooldownFactor: String(point.cooldownFactor),
    })),
    lowPowerUtilization: String(resolved.advisoryThresholds.lowPowerUtilization),
    minimumCoilFrameSizeMarginMm: String(
      resolved.advisoryThresholds.minimumCoilFrameSizeMarginMm,
    ),
    minimumCoilCoverageMarginMm: String(
      resolved.advisoryThresholds.minimumCoilCoverageMarginMm,
    ),
    powerUtilizationBoundaryMargin: String(
      resolved.advisoryThresholds.powerUtilizationBoundaryMargin,
    ),
  } as RecommendationOptionsFormValues;

  for (const field of MODEL_COEFFICIENT_FIELDS) {
    values[field.key] = String(resolved.modelCoefficients[field.key]);
  }

  return values;
}

export function formValuesEqual(
  left: RecommendationOptionsFormValues,
  right: RecommendationOptionsFormValues,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function addCalibrationPointToForm(
  values: RecommendationOptionsFormValues,
): RecommendationOptionsFormValues {
  return {
    ...values,
    frequencyCalibrationPoints: [
      ...values.frequencyCalibrationPoints,
      { frequencyHz: "0", heatingCycleFactor: "1", cooldownFactor: "1" },
    ],
  };
}

export function removeCalibrationPointFromForm(
  values: RecommendationOptionsFormValues,
  index: number,
): RecommendationOptionsFormValues {
  return {
    ...values,
    frequencyCalibrationPoints: values.frequencyCalibrationPoints.filter(
      (_, rowIndex) => rowIndex !== index,
    ),
  };
}
